/**
 * Game Table Component
 * Main container for the poker game with circular player layout
 */

import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../../store/gameStore';
import { useGameSocket } from '../../hooks/useGameSocket';
import { useAuth } from '../../contexts/AuthContext';
import { useSound } from '../../hooks/useSound';
import { GameTable as GameTableData } from '../../services/gameService';
import OpponentList from './OpponentList';
import PlayerPanel from './PlayerPanel';
import PlayingCard from './PlayingCard';
import SpecialCard from './SpecialCard';
import { specialOfCard } from './specialOfCard';
import HandsPanel from './HandsPanel';
import ActionTicker from './ActionTicker';
import PotsTicker from './PotsTicker';
import { formatChips } from '../../utils/format';
import CommunityGrid from './CommunityGrid';
import RevealProgress from './RevealProgress';
import LivePots from './LivePots';
import KnockedOutPanel from './KnockedOutPanel';
import PotResults from './PotResults';
import LobbyButton from './LobbyButton';
import { useSecondsLeft } from '../../hooks/useSecondsLeft';
import GameOverPanel from './GameOverPanel';
import { showToast } from '../../lib/toast';
import { gameService } from '../../services/gameService';
import BettingControls from './BettingControls';
import JoinTableModal from './JoinTableModal';
import { Player, type ConnectionStatus } from '../../store/types';
import { PLAYERS_PER_TABLE, SpecialCardType, blockedBettingMove, isRealCard } from '@pokercubed/shared';

/**
 * Your hole cards and specials share one size. With two specials the four
 * don't fit at full width, so they shrink together (same width, same shrink)
 * and stay identical.
 */
/** A computer's seat columns, either side of the grid. */
const SEAT_COLUMN = 'w-[clamp(150px,14vw,240px)] shrink-0 min-h-0';

const HAND_CARD_SIZE = 'w-[61px] landscape:w-[52px] min-w-0 shrink portrait:!w-[var(--cell-w,61px)] desk:!w-[calc(var(--cell-w,61px)*0.8)]';

/** Whether a CSS media query matches right now, following changes (e.g. the phone rotating). */
/** The specials that see into opponents' hands (from the diamond on). */
const INSIGHT_SPECIALS = new Set<string>([
  SpecialCardType.ORACLE,
  SpecialCardType.SOOTHSAYER,
  SpecialCardType.SPY,
  SpecialCardType.OMNIPOTENT,
]);

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/** How long a connection problem must last before its banner shows. */
const CONNECTION_BANNER_DELAY_MS = 2000;

/**
 * The connection status to show: a problem only once it has lasted a
 * moment, so the blips of opening or leaving a game (and quick recoveries)
 * don't flash a banner and push the table down.
 */
function useSettledConnectionStatus(status: ConnectionStatus): ConnectionStatus {
  const [shown, setShown] = useState<ConnectionStatus>(status);
  useEffect(() => {
    if (status === 'connected' || status === 'connecting') {
      setShown(status);
      return;
    }
    const t = setTimeout(() => setShown(status), CONNECTION_BANNER_DELAY_MS);
    return () => clearTimeout(t);
  }, [status]);
  return shown;
}

interface GameTableProps {
  tableId: string;
  token: string;
  tableData: GameTableData;
  /** Set false for mock/offline views driven by local store state only — skips the live socket connection entirely. Defaults to true. */
  liveSocket?: boolean;
}

const GameTable: React.FC<GameTableProps> = ({ tableId, token, tableData, liveSocket = true }) => {
  const gameState = useGameStore();
  const connectionStatus = useSettledConnectionStatus(gameState.connectionStatus);
  const { user } = useAuth();
  const { performAction, joinTable, startNextHand, spectate } = useGameSocket({
    tableId,
    token,
    enabled: liveSocket,
  });
  const { play: playSound } = useSound();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [dealingNext, setDealingNext] = useState(false);

  // Upright phone: the seats sit in two columns of 5 beside the grid (only
  // one layout is rendered, so each seat appears once on the page).
  const portrait = useMediaQuery('(orientation: portrait)');

  // Your hole cards and specials match the grid's own card size upright, so
  // four (two specials) always fit under it: --cell-w, measured here.
  const gridPanelRef = useRef<HTMLDivElement>(null);
  const [cellWidth, setCellWidth] = useState(0);
  useEffect(() => {
    // Watches the panel (it stays put), not a cell: the grid re-renders its
    // cells, and a removed cell would read as 0 wide.
    const panel = gridPanelRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const cell = panel.querySelector('[data-testid="grid-cell"]') as HTMLElement | null;
      if (cell && cell.offsetWidth > 0) setCellWidth(cell.offsetWidth);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    measure();
    return () => observer.disconnect();
  }, []);

  // A phone held sideways: also too short for the pot panels.
  const short = useMediaQuery('(orientation: landscape) and (max-height: 600px)');
  // On phones the pots are a fixed-height ticker, so nothing moves when side
  // pots appear or the results come in.
  const potsAsTicker = portrait || short;
  // A computer screen: like a phone upright, seats 1-5 left of the grid and
  // 6-10 right of it, and the whole table fits the window (no page scroll).
  const desk = useMediaQuery('(orientation: landscape) and (min-height: 601px)');

  // Track previous values to detect changes
  const prevPhaseRef = useRef(gameState.phase);
  const prevActionHistoryLengthRef = useRef(gameState.actionHistory.length);
  const prevHandResultRef = useRef(gameState.handResult);

  // Get player for specific seat
  const getPlayerForSeat = (seatIndex: number): Player | null => {
    return gameState.players.find((p) => p.seatPosition === seatIndex) || null;
  };

  // Every seat, you included, in fixed seat order 1-10 — that's the turn
  // order, starting from whoever holds the chip, so the list reads as the
  // round: down from the chip, round to the top, ending at the seat before it.
  const tableSeats = Array.from({ length: PLAYERS_PER_TABLE }).map((_, seatPosition) => ({
    seatPosition,
    player: getPlayerForSeat(seatPosition),
  }));

  const you = gameState.yourSeat !== null ? getPlayerForSeat(gameState.yourSeat) : null;

  // Insight specials (Oracle, Soothsayer, Spy, Omnipotent) let you see into
  // opponents' hands from the diamond on; the seat tiles are too small to
  // read, so tapping the card opens them full size.
  const [handsOpen, setHandsOpen] = useState(false);
  const insightActive =
    gameState.specialCardEffects && ['ROUND_2', 'ROUND_3', 'SHOWDOWN', 'ENDED'].includes(gameState.phase);
  const viewHandsFor = (special: string | undefined) =>
    insightActive && special && INSIGHT_SPECIALS.has(special) ? () => setHandsOpen(true) : undefined;

  // Closed again for each new hand (so it can't pop up by itself next time).
  useEffect(() => {
    if (!insightActive) setHandsOpen(false);
  }, [insightActive]);

  // Winners of the hand that just ended — tracked per axis, not merged into
  // one generic "winner" flag, so a jewel-axis winner is visibly distinct
  // from (or additional to) the rank-axis winner even when they're
  // different players. Gated on phase (not just handResult's presence) so
  // a stale result from the previous hand never lingers once a new one starts.
  const isShowdown = gameState.phase === 'ENDED' && !!gameState.handResult;
  const rankWinnerIds = isShowdown
    ? new Set(gameState.handResult!.winners.map((w) => w.userId))
    : undefined;
  const jewelWinnerIds = isShowdown
    ? new Set((gameState.handResult!.jewelWinners ?? []).map((w) => w.userId))
    : undefined;
  const youWonRank = !!you && !!rankWinnerIds?.has(you.id);
  const youWonJewel = !!you && !!jewelWinnerIds?.has(you.id);
  const youWon = youWonRank || youWonJewel;

  // Check if it's current player's turn
  const isYourTurn =
    gameState.yourSeat !== null &&
    gameState.players.some(
      (p) => p.seatPosition === gameState.yourSeat && p.isCurrentTurn
    );

  // Your own row is the live source for your stack and this round's bet —
  // action-broadcast keeps it current, while yourChips only refreshes on a
  // full game-state.
  const yourChips = you?.chipCount ?? gameState.yourChips;
  const yourBet = you?.currentBet ?? 0;
  const toCall = Math.max(0, gameState.currentBet - yourBet);
  // Origami/Hellraiser/Shepherd: the move your special card takes away this hand.
  const blockedMove = gameState.specialCardEffects
    ? blockedBettingMove((you?.holeCards ?? []).filter(isRealCard))
    : null;

  // Betting actions with optimistic UI
  const handleFold = async () => {
    try {
      await performAction('FOLD', undefined, user?.id);
    } catch (error) {
      // Error already handled by performAction (toast shown, rollback applied)
      console.error('Failed to fold:', error);
      playSound('error'); // Play error sound on action failure
    }
  };

  const handleCall = async () => {
    try {
      // Same condition BettingControls uses to label this button "Check"
      // vs "Call N" — sending CALL when there's nothing to call is
      // rejected by the engine ("Nothing to call - use check instead"),
      // so the action sent has to match what the button actually says.
      // The engine works out the call amount itself.
      if (toCall === 0) {
        await performAction('CHECK', undefined, user?.id);
      } else {
        await performAction('CALL', undefined, user?.id);
      }
    } catch (error) {
      console.error('Failed to call:', error);
      playSound('error'); // Play error sound on action failure
    }
  };

  const handleRaise = async (amount: number) => {
    try {
      // With nothing bet yet this round it's an opening bet; otherwise
      // `amount` is the raise on top of the call (the engine's RAISE meaning).
      await performAction(gameState.currentBet === 0 ? 'BET' : 'RAISE', amount, user?.id);
    } catch (error) {
      console.error('Failed to raise:', error);
      playSound('error'); // Play error sound on action failure
    }
  };

  const handleAllIn = async () => {
    try {
      await performAction('ALL_IN', yourChips, user?.id);
    } catch (error) {
      console.error('Failed to go all-in:', error);
      playSound('error'); // Play error sound on action failure
    }
  };

  // Leaving returns your chips to your bank; for testing, the solo page then
  // seats you at a fresh table (in the finished app: back to the lobby).
  // A solo game is its own table: a new game is a new table. A shared
  // (lobby) table plays on — leaving goes back to the lobby, and a new game
  // starts at the same table.
  const solo = tableData.name === 'Solo Table';
  const leaveForNewGame = async () => {
    await gameService.leaveTable(tableId).catch(() => {});
    window.location.assign(solo ? '/solo' : '/lobby');
  };
  const newGame = solo ? leaveForNewGame : async () => { await startNextHand(); };
  // The next hand deals itself when this runs out (anyone can deal sooner).
  const dealIn = useSecondsLeft(gameState.phase === 'ENDED' ? gameState.dealDeadline : null);
  const toLobby = async () => {
    await gameService.leaveTable(tableId).catch(() => {});
    window.location.assign('/lobby');
  };
  // A fixed buy-in (every shared table has one): joining is one tap.
  const fixedBuyIn = tableData.minBuyIn === tableData.maxBuyIn ? tableData.minBuyIn : null;
  const [joining, setJoining] = useState(false);
  const join = async () => {
    if (fixedBuyIn === null) return setShowJoinModal(true);
    setJoining(true);
    try {
      await joinTable(fixedBuyIn);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to join table', 'error');
    } finally {
      setJoining(false);
    }
  };

  // Knocked out and not in this hand (spectating): no cards, no best hands to show.
  // The hand that knocked you out still shows until the next one is dealt.
  const notDealtIn = gameState.knockedOutPlace !== null && !(you?.holeCards && you.holeCards.length > 0);

  const handleDealNext = async () => {
    setDealingNext(true);
    try {
      await startNextHand();
      // The server only confirms once the new game is dealt — and, if it
      // played out during the deal (everyone all-in on the ante), already
      // over again. The phase then never leaves ENDED, so the phase-change
      // effect below wouldn't reset this; the confirmation does.
      setDealingNext(false);
    } catch (error) {
      console.error('Failed to start next hand:', error);
      playSound('error');
      setDealingNext(false);
    }
  };

  // Sound effects for game events
  useEffect(() => {
    // Play sound when phase changes (new cards dealt)
    if (prevPhaseRef.current !== gameState.phase) {
      if (gameState.phase !== 'ANTE' && gameState.phase !== 'ENDED') {
        playSound('card-deal');
      }
      prevPhaseRef.current = gameState.phase;
    }
  }, [gameState.phase, playSound]);

  useEffect(() => {
    // Play sound when new action added to history
    if (gameState.actionHistory.length > prevActionHistoryLengthRef.current) {
      const latestAction = gameState.actionHistory[gameState.actionHistory.length - 1];
      if (!latestAction.isOptimistic) { // Only play for confirmed actions
        switch (latestAction.action) {
          case 'FOLD':
            playSound('action-fold');
            break;
          case 'CALL':
          case 'CHECK':
            playSound('action-call');
            break;
          case 'RAISE':
          case 'BET':
          case 'ALL_IN':
            playSound('action-raise');
            break;
        }
      }
      prevActionHistoryLengthRef.current = gameState.actionHistory.length;
    }
  }, [gameState.actionHistory, playSound]);

  useEffect(() => {
    // Play sound when hand result appears (pot won)
    if (!prevHandResultRef.current && gameState.handResult) {
      playSound('pot-win');
    }
    prevHandResultRef.current = gameState.handResult;
  }, [gameState.handResult, playSound]);

  useEffect(() => {
    // Once the next hand actually starts (phase moves off ENDED), the
    // "Deal New Game" button's job is done — reset it so it's ready again
    // next time the hand ends.
    if (gameState.phase !== 'ENDED' && dealingNext) {
      setDealingNext(false);
    }
  }, [gameState.phase, dealingNext]);

  return (
    // Phone layouts: in portrait the action area (betting buttons etc.) is
    // fixed to the bottom of the screen, so the page leaves room for it
    // (--action-h, measured below); on a phone held sideways ("short") the
    // whole table fits the screen's height and each column scrolls itself.
    <div
      // Upright on a phone the page is locked to the visible height (dvh, not
      // the screen height behind the address bar), so the table can't scroll.
      className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-2 sm:p-4 desk:min-h-0 desk:flex-1 desk:overflow-hidden desk:flex desk:flex-col portrait:h-dvh portrait:min-h-0 portrait:overflow-hidden portrait:overscroll-none portrait:pb-[calc(var(--action-h)+env(safe-area-inset-bottom))] short:h-dvh short:min-h-0 short:overflow-hidden short:!p-1.5"
      // Upright, the action bar is fixed to the bottom at this height (taller
      // things, like the raise slider, slide up over the table rather than
      // pushing it about).
      style={{ '--action-h': '150px' } as React.CSSProperties}
    >
      {handsOpen && insightActive && (
        <HandsPanel
          title="Opponents' hands — what your card shows you"
          players={gameState.players.filter((p) => p.id !== you?.id).sort((a, b) => a.seatPosition - b.seatPosition)}
          onClose={() => setHandsOpen(false)}
        />
      )}
      <div className="max-w-[1800px] w-full mx-auto short:h-full short:flex short:flex-col desk:flex-1 desk:min-h-0 desk:flex desk:flex-col">
        {/* Connection status banners */}
        {connectionStatus === 'reconnecting' && (
          <motion.div
            className="mb-2 sm:mb-4 p-3 sm:p-4 bg-yellow-900/50 border-2 border-yellow-500 rounded-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center text-white text-sm sm:text-base font-bold">
              🔄 Reconnecting to server...
              {gameState.reconnectAttempt > 0 && (
                <span className="ml-2 text-xs sm:text-sm opacity-80">
                  (attempt {gameState.reconnectAttempt})
                </span>
              )}
            </div>
          </motion.div>
        )}

        {connectionStatus === 'disconnected' && (
          <motion.div
            className="mb-2 sm:mb-4 p-3 sm:p-4 bg-red-900/50 border-2 border-red-500 rounded-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center text-white text-sm sm:text-base font-bold">
              ⚠️ Disconnected from server. Reconnecting...
            </div>
          </motion.div>
        )}

        {connectionStatus === 'failed' && (
          <motion.div
            className="mb-2 sm:mb-4 p-3 sm:p-4 bg-red-900/80 border-2 border-red-600 rounded-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center">
              <div className="text-white text-sm sm:text-base font-bold mb-2">❌ Connection lost</div>
              <button
                onClick={() => window.location.reload()}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-semibold transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </motion.div>
        )}

        {/* 3-panel gameplay area — stacked top-to-bottom (players / gameplay
            / you) in portrait, since a phone in portrait doesn't have the
            width for 3 side-by-side columns without crushing the matrix;
            side-by-side (LHS/middle/RHS) once there's landscape width to
            spare. Same component tree either way, just re-flowed via the
            `landscape:` variant. */}
        {/* --cell-w (the grid's card size) set here too, so the Best boxes' cards match it. */}
        <div
          className="flex flex-col landscape:flex-row gap-1.5 sm:gap-3 short:!gap-1.5 short:flex-1 short:min-h-0 desk:flex-1 desk:min-h-0 desk:justify-center portrait:h-[calc(100dvh-var(--action-h)-env(safe-area-inset-bottom)-1rem)]"
          style={{ '--cell-w': cellWidth ? `${cellWidth}px` : undefined } as React.CSSProperties}
        >
          {/* Opponents — a horizontal scroll strip across the top in
              portrait (no room for a tall column), the usual narrow
              scrollable column once landscape gives it real width. Card
              reveals and special-card badges need real room here, so the
              landscape column keeps its firm minimum. */}
          {short && (
          <div className="w-full landscape:flex-1 landscape:min-w-[130px] landscape:max-w-[260px] short:flex short:flex-col short:min-h-0">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5 portrait:hidden short:hidden">
              Players
            </div>
            <div className="h-[76px] landscape:h-[320px] landscape:sm:h-[420px] landscape:lg:h-[560px] short:!h-auto short:flex-1 short:min-h-0">
              <OpponentList
                seats={tableSeats}
                yourSeat={gameState.yourSeat}
                rankWinnerIds={rankWinnerIds}
                jewelWinnerIds={jewelWinnerIds}
              />
            </div>
          </div>
          )}

          {/* Upright, and on a computer: seats 1-5 down the left of the grid, 6-10 down the right. */}
          <div
            className={
              portrait
                ? 'flex items-stretch justify-center gap-1.5'
                : desk
                  ? 'flex items-stretch gap-3 min-w-0 min-h-0'
                  : 'contents'
            }
          >
          {(portrait || desk) && (
            <div className={portrait ? 'w-[70px] shrink-0' : SEAT_COLUMN}>
              <OpponentList variant={portrait ? 'tiles' : 'column'} seats={tableSeats.slice(0, 5)} yourSeat={gameState.yourSeat} rankWinnerIds={rankWinnerIds} jewelWinnerIds={jewelWinnerIds} />
            </div>
          )}

          {/* Gameplay: the matrix — has real room now that it isn't
              fighting two firm-minimum columns for space (portrait) or
              carrying an oversized fixed column width (landscape), but
              doesn't need to fill the space just because it can; capped
              well below full-screen/full-column so it doesn't dominate
              either layout. Your hole cards live in the leftover space
              below the reveal tracker. */}
          {/* Sized to the screen's height on phones, so the grid and your
              hole cards fit without scrolling. Upright, one card size runs
              through the grid, your hand and the Best boxes: the width is
              chosen so all three (6 rows of cards) fit the height together —
              roughly height = 2.56 x width + 254px of everything else. */}
          <div
            ref={gridPanelRef}
            style={{ '--cell-w': cellWidth ? `${cellWidth}px` : undefined } as React.CSSProperties}
            className="w-full max-w-[260px] mx-auto landscape:w-[260px] landscape:max-w-full landscape:min-w-0 landscape:shrink overflow-hidden p-2 sm:p-4 bg-gradient-to-br from-green-900/60 to-green-950/60 rounded-2xl border-2 border-yellow-800/60 flex flex-col portrait:mx-0 portrait:shrink-0 portrait:!p-1.5 portrait:w-[clamp(150px,min(calc(100vw-176px),calc((100dvh-304px)/2.09)),300px)] portrait:max-w-none short:!w-[clamp(150px,calc((100dvh-110px)/1.3),260px)] short:!p-1.5 short:shrink-0 short:self-start desk:!w-[clamp(280px,calc((100dvh-274px)*0.566+96px),560px)] desk:shrink-0 desk:mx-0"
          >
            <CommunityGrid cards={gameState.communityCards} phase={gameState.phase} />
            <RevealProgress phase={gameState.phase} />
            <div
              className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 pt-3.5 ${
                youWon ? 'ring-2 ring-yellow-400/70 rounded-xl bg-yellow-900/20' : ''
              }`}
            >
              {/* Each special as its own extra card, the same size as a hole card, on its own card's
                  side: [left card's special] left card, right card [right card's special]. The hole
                  card it belongs to pulses. Until the 4 corners are revealed it's the gold "?" —
                  keyed by hole card, so the same card turns over. */}
              {(() => {
                const hole = (you?.holeCards && you.holeCards.length > 0 ? you.holeCards : notDealtIn ? [] : [undefined, undefined]).slice(0, 2);
                const leftSpecial = specialOfCard(hole[0]);
                const rightSpecial = specialOfCard(hole[1]);
                return (
                  <>
                    {leftSpecial && <SpecialCard key="special-0" special={leftSpecial} className={HAND_CARD_SIZE} onView={viewHandsFor(leftSpecial)} />}
                    {hole.map((card, i) => (
                      <PlayingCard key={i} card={card} className={HAND_CARD_SIZE} />
                    ))}
                    {rightSpecial && <SpecialCard key="special-1" special={rightSpecial} className={HAND_CARD_SIZE} onView={viewHandsFor(rightSpecial)} />}
                  </>
                );
              })()}
            </div>
            {/* Not on phones: the Best Hand / Best Color boxes say it, and the extra line would resize the grid panel. */}
            {!potsAsTicker && (you?.handDescription || you?.jewelHandDescription) && (
              <div
                className={`text-center text-xs font-semibold pb-1 ${
                  youWon ? 'text-yellow-300' : 'text-gray-400'
                }`}
              >
                {[you?.handDescription, you?.jewelHandDescription].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          {(portrait || desk) && (
            <div className={portrait ? 'w-[70px] shrink-0' : SEAT_COLUMN}>
              <OpponentList variant={portrait ? 'tiles' : 'column'} seats={tableSeats.slice(5, 10)} yourSeat={gameState.yourSeat} rankWinnerIds={rankWinnerIds} jewelWinnerIds={jewelWinnerIds} />
            </div>
          )}
          </div>

          {/* You: chips, best hands, betting controls — carries the most
              info so it gets the most room once landscape has columns. */}
          <div className="w-full landscape:flex-[1.6] landscape:min-w-[150px] landscape:max-w-[420px] desk:!max-w-[460px] flex flex-col gap-2 sm:gap-3 short:!gap-1.5 short:min-h-0 desk:min-h-0 portrait:flex-1 portrait:min-h-0">
            <div className="flex flex-col gap-2 sm:gap-3 short:!gap-1.5 short:flex-1 short:min-h-0 short:overflow-y-auto desk:flex-1 desk:min-h-0 desk:overflow-y-auto desk:pr-1 portrait:flex-1 portrait:min-h-0">
            {potsAsTicker && (
              <PotsTicker
                livePots={gameState.sidePots}
                pot={gameState.pot}
                results={gameState.phase === 'ENDED' ? gameState.handResult?.pots : null}
                players={gameState.players}
                youId={you?.id}
                ante={gameState.ante}
              />
            )}

            {!notDealtIn && <PlayerPanel pot={gameState.pot} wonRank={youWonRank} wonJewel={youWonJewel} compact={potsAsTicker} />}

            {!potsAsTicker && gameState.ante && (
              <div data-testid="ante" className="px-1 text-xs text-gray-400">
                Ante <span className="font-semibold text-yellow-400">{formatChips(gameState.ante.current)}</span>
                {gameState.ante.next > gameState.ante.current && <> · {formatChips(gameState.ante.next)} next hand</>}
              </div>
            )}

            {!potsAsTicker && gameState.phase !== 'ENDED' && (
              <LivePots pots={gameState.sidePots} players={gameState.players} youId={you?.id} />
            )}

            {!potsAsTicker && gameState.phase === 'ENDED' && gameState.handResult?.pots && gameState.handResult.pots.length > 0 && (
              <PotResults
                pots={gameState.handResult.pots}
                youId={you?.id}
              />
            )}
            </div>

            {/* What you can do now — betting buttons, Deal New Game, or the
                knocked-out / game-over choices. Fixed to the bottom of the
                screen in portrait so it's always in reach; at the bottom of
                this column otherwise. */}
            <div
              className="portrait:fixed portrait:inset-x-0 portrait:bottom-0 portrait:z-40 portrait:px-2 portrait:pt-2 portrait:pb-[max(0.5rem,env(safe-area-inset-bottom))] portrait:bg-gray-950/95 portrait:border-t portrait:border-gray-800 short:shrink-0"
            >
            <div className="portrait:max-w-md portrait:mx-auto">
            {/* The hand's recent actions, as a one-line ticker tape. */}
            <div className="mb-1.5">
              <ActionTicker actions={gameState.actionHistory} since={gameState.roundStartedAt} />
            </div>
            {/* The same height whether or not it's your turn (upright). */}
            <div className="portrait:min-h-[96px] portrait:flex portrait:flex-col portrait:justify-end">
            {gameState.tableOver && (gameState.tableOver.winner || gameState.knockedOutPlace === null) ? (
              <GameOverPanel
                tableOver={gameState.tableOver}
                youId={you?.id ?? user?.id}
                onNewGame={newGame}
                onLobby={toLobby}
              />
            ) : gameState.knockedOutPlace !== null ? (
              // Out of chips while the game goes on: leave, or spectate.
              <KnockedOutPanel
                place={gameState.knockedOutPlace}
                spectating={gameState.spectating}
                onLobby={toLobby}
                onNewGame={solo ? leaveForNewGame : undefined}
                onSpectate={async () => {
                  await spectate().catch(() => {});
                }}
              />
            ) : gameState.yourSeat !== null && gameState.phase === 'ENDED' ? (
              gameState.awaitingEndState ? null : <div className="flex gap-2">
              <button
                data-testid="deal-next"
                onClick={handleDealNext}
                disabled={dealingNext}
                // Upright, it fills the whole bar (the betting buttons' space).
                className="flex-1 py-3 sm:py-4 portrait:h-[96px] portrait:text-xl bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-base sm:text-lg font-bold rounded-xl shadow-lg transition-all"
              >
                {dealingNext ? 'Dealing…' : 'Deal New Game'}
                {!dealingNext && dealIn !== null && (
                  <span data-testid="deal-countdown" className={`block text-xs sm:text-sm font-semibold ${dealIn <= 5 ? 'text-red-100' : 'text-yellow-100/90'}`}>
                    deals itself in {dealIn}s
                  </span>
                )}
              </button>
              {/* A solo game has no header (nor a shared table on a phone): the way to the lobby is here, between hands. */}
              <LobbyButton onLeave={toLobby} className="w-[30%] py-3 sm:py-4 portrait:h-[96px] text-sm sm:text-base" />
              </div>
            ) : gameState.yourSeat !== null ? (
              <BettingControls
                currentBet={gameState.currentBet}
                yourBet={yourBet}
                yourChips={yourChips}
                minRaise={gameState.minRaise ?? tableData.anteAmount}
                blockedMove={blockedMove}
                turnDeadline={gameState.turnPlayerId === you?.id ? gameState.turnDeadline : null}
                isYourTurn={isYourTurn}
                isPending={gameState.pendingAction !== null}
                onFold={handleFold}
                onCall={handleCall}
                onRaise={handleRaise}
                onAllIn={handleAllIn}
              />
            ) : (
              <div className="p-4 sm:p-6 bg-gray-800/50 rounded-xl border border-gray-700">
                <div className="text-center">
                  <div className="text-base sm:text-lg font-semibold text-gray-300 mb-2">Spectating</div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4">
                    You are watching this table.
                  </p>
                  {/* Bots make way for people: only people fill a table. */}
                  {tableData.currentPlayers - (tableData.players ?? []).filter((p) => p.isBot).length < tableData.maxPlayers ? (
                    <button
                      onClick={join}
                      disabled={joining}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 disabled:opacity-60 text-white text-sm sm:text-base font-bold rounded-lg shadow-lg transition-all"
                    >
                      {joining ? 'Joining…' : 'Join Table'}
                    </button>
                  ) : (
                    <p className="text-xs sm:text-sm text-red-400 font-semibold">
                      Table is full ({tableData.maxPlayers}/{tableData.maxPlayers} players)
                    </p>
                  )}
                  {/* Phones have no header: the way back from watching. */}
                  <div className="mt-2 hidden portrait:block short:block">
                    <LobbyButton onLeave={async () => window.location.assign('/lobby')} confirm={false} className="px-6 py-2 text-sm" />
                  </div>
                </div>
              </div>
            )}
            </div>
            </div>
            </div>
          </div>
        </div>

        {/* Join table modal */}
        {showJoinModal && user && (
          <JoinTableModal
            table={tableData}
            userChips={user.totalChips}
            onJoin={async (buyIn) => {
              await joinTable(buyIn);
              setShowJoinModal(false);
            }}
            onClose={() => setShowJoinModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default GameTable;
